import type { ClinicalSummaryReview } from '@/pages/order/clinicalSummaryReviewTypes'
import { clinicianOwned } from '@/pages/order/livePhenotypeMerge'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import type { ClinicalSummaryReasonKind } from './clinicalSummaryReasonLabels'

/**
 * `document`: every chip of a section where it is first cited (R06). `document-then-placed`: the
 * extracted chips where they are first cited, then the ones the clinician added or filed, in the
 * order they joined the diagnosis (S01, S04, S07).
 */
export type ClinicalSummaryChipOrder = 'document' | 'document-then-placed'

const RELATIVE_REASONS: ReadonlySet<ClinicalSummaryReasonKind> = new Set(['maternal-history', 'family-history'])

/** Where the term is first cited in the text its citations index; uncited terms come after every cited one. */
export function firstCitationBegin(review: ClinicalSummaryReview | null | undefined, code: string): number {
  const begins = (review?.terms[code]?.citations ?? []).map(({ begin }) => begin).filter(Number.isInteger)
  return begins.length > 0 ? Math.min(...begins) : Number.POSITIVE_INFINITY
}

/** Stable: terms cited at the same place, or not at all, keep the order they arrived in. */
function inReadingOrder<T extends CategorizedHPOOption>(
  terms: readonly T[],
  review: ClinicalSummaryReview | null | undefined
): T[] {
  return terms
    .map((term, index) => ({ term, index, begin: firstCitationBegin(review, term.id) }))
    .sort((a, b) => (a.begin === b.begin ? 0 : a.begin - b.begin) || a.index - b.index)
    .map(({ term }) => term)
}

/** Terms no longer in the summary lead their section in the order they arrived (R11, S05); they cite nothing. */
export function orderSectionChips<T extends CategorizedHPOOption>(
  terms: readonly T[],
  review: ClinicalSummaryReview | null | undefined,
  order: ClinicalSummaryChipOrder
): T[] {
  const orphan = (term: T) => review?.terms[term.id]?.reviewState === 'not-in-summary'
  const orphans = terms.filter(orphan)
  const rest = terms.filter((term) => !orphan(term))
  if (order === 'document') return [...orphans, ...inReadingOrder(rest, review)]
  const placed = (term: T) => {
    const termReview = review?.terms[term.id]
    return termReview ? clinicianOwned(termReview) : false
  }
  return [
    ...orphans,
    ...inReadingOrder(
      rest.filter((term) => !placed(term)),
      review
    ),
    ...rest.filter(placed),
  ]
}

/**
 * The patient's own findings in reading order, then the ones about a relative (R06, R12, S01, S04).
 * The extraction leaves relatives' findings out, so those are rows it did not annotate, such as ones
 * saved before. `citesRelative` reads the summary, so a row keeps its place when it has no reason.
 */
export function orderNeedsInputRows<T extends CategorizedHPOOption>(
  terms: readonly T[],
  review: ClinicalSummaryReview | null | undefined,
  reasonOf: (term: T) => ClinicalSummaryReasonKind | null,
  citesRelative: (term: T) => boolean = () => false
): T[] {
  const aboutRelative = (term: T) => {
    const reason = reasonOf(term)
    return reason !== null ? RELATIVE_REASONS.has(reason) : citesRelative(term)
  }
  return [
    ...inReadingOrder(
      terms.filter((term) => !aboutRelative(term)),
      review
    ),
    ...inReadingOrder(terms.filter(aboutRelative), review),
  ]
}
